import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import api from '../../../../api/axios';
import ItemLatestSales from './ItemLatestSales';
import ItemHistoryChart from './ItemHistoryChart';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../store';

export type Timeframe = '1m' | '3m' | '1y' | 'all';

interface Props {
  itemAssetId: string | number;
  wearName?: string;
}

export interface ItemHistory {
  day: string;
  avgPriceMinor: number;
  count: number;
}

export interface LatestSale {
  soldDate: string;
  price: string;
  ssFront: string | null;
  ssBack: string | null;
  float: string;
  wearName: string;
  paintSeed: string;
  stickers: string;
}

const ItemAnalytics: React.FC<Props> = ({ itemAssetId, wearName }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'history' | 'sales'>('history');
  const [timeframe, setTimeframe] = useState<Timeframe>('1m');
  const [history, setHistory] = useState<ItemHistory[]>([]);
  const [latestSales, setLatestSales] = useState<LatestSale[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const formattedWear = wearName && wearName !== '' ? wearName.toUpperCase() : null;
      const params = {
        itemAssetId: itemAssetId.toString(),
        ...(formattedWear ? { wearType: formattedWear } : {})
      };

      try {
        const historyEndpoint = isAuthenticated
            ? `/item/${itemAssetId.toString()}/history`
            : `/guest/item/${itemAssetId.toString()}/history`;

        const salesEndpoint = isAuthenticated
            ? '/item/latest-sales'
            : '/guest/item/latest-sales';

        const [historyRes, salesRes] = await Promise.allSettled([
          api.get<ItemHistory[]>(historyEndpoint),
          api.get<LatestSale[]>(salesEndpoint, { params })
        ]);

        if (historyRes.status === 'fulfilled') setHistory(historyRes.value.data || []);
        if (salesRes.status === 'fulfilled') setLatestSales(salesRes.value.data || []);
      } catch (err) {
        console.error('Unexpected Error in fetchData:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [itemAssetId, wearName, isAuthenticated]);

  return (
      <div className="flex flex-col bg-neutral-900/40 backdrop-blur-md border border-neutral-800 p-4 rounded shadow-xl">
        {/* Header Controls */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-6">

          {/* Left Side: Title + Mode Toggle directly attached */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-[#3f51b5]/10 rounded-lg border border-[#3f51b5]/20">
                <Timeline className="text-[#3f51b5] w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-black text-white uppercase tracking-widest">Item Analytics</h3>
                <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-tighter">
                  {viewMode === 'history' ? 'Price History' : 'Latest Trades'} / {timeframe.toUpperCase()}
                </p>
              </div>
            </div>

            {/* Mode Toggle (History/Sales) - Glued to the title block */}
            <div className="flex bg-neutral-800/50 p-1 rounded-lg border border-white/5">
              <button
                  onClick={() => setViewMode('history')}
                  className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${
                      viewMode === 'history' ? 'bg-neutral-700 text-white shadow-lg' : 'text-neutral-500 hover:text-neutral-300'
                  }`}
              >
                History
              </button>
              <button
                  onClick={() => setViewMode('sales')}
                  className={`px-3 py-1 text-[9px] font-black uppercase transition-all rounded ${
                      viewMode === 'sales' ? 'bg-neutral-700 text-white shadow-lg' : 'text-neutral-500 hover:text-neutral-300'
                  }`}
              >
                Sales
              </button>
            </div>
          </div>

          {/* Right Side: Timeframe Selector */}
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            {viewMode === 'history' && (
                <div className="flex bg-neutral-800/50 p-1 rounded-lg border border-white/5">
                  {(['1m', '3m', '1y', 'all'] as Timeframe[]).map((tf) => (
                      <button
                          key={tf}
                          onClick={() => setTimeframe(tf)}
                          className={`px-3 py-1 text-[10px] font-black uppercase transition-all rounded ${
                              timeframe === tf ? 'bg-[#3f51b5] text-white shadow-lg' : 'text-neutral-500 hover:text-neutral-300'
                          }`}
                      >
                        {tf}
                      </button>
                  ))}
                </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="h-[242px] w-full border-t border-neutral-800">
          {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <CircularProgress size={30} thickness={4} sx={{ color: '#3f51b5' }} />
              </div>
          ) : viewMode === 'history' ? (
              <ItemHistoryChart data={history} timeframe={timeframe} />
          ) : (
              <ItemLatestSales sales={latestSales} />
          )}
        </div>
      </div>
  );
};

export default ItemAnalytics;